/**
 * The review table, grouped by charity and then by event.
 *
 * A plain Semantic table over client held rows. Grid.Managed is not usable
 * here: it is bound to server side search endpoints and has no local data mode.
 *
 * Charity is the outer level because it is the export boundary: each charity
 * gets its own CSV, downloaded from its own header, so the operator cannot pick
 * up a file containing somebody else's ambassadors. Each charity has exactly
 * one currency, which also makes it the only unit that can carry an honest
 * subtotal.
 *
 * Sized for real batches of 50 to 100 rows: the header sticks, the body scrolls
 * in its own container, and each row is memoised so a per row charge result
 * does not re-render the whole table.
 */

import React from 'react';
import { Button, Icon, Table } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import { formatAmountWithCurrency, isChargeable, sumChargeableAmount } from '../helpers';
import { CharityGroup, EventSubGroup, RecentChargeEntry } from '../types';
import SummaryTableRow from './SummaryTableRow';
import styles from '../BulkChargeGuarantee.module.css';

interface SummaryTableProps {
  groups: CharityGroup[];
  recentEntries: RecentChargeEntry[];
  now: number;
  canRemove: boolean;
  showOutcome: boolean;
  onRemoveId: (id: number) => void;
  onRefreshId: (id: number) => void;
  onDownloadCharity: (group: CharityGroup) => void;
}

const COLUMN_COUNT = 8;

const eventLabel = (event: EventSubGroup): string =>
  `${I18n.t(i18nKeys.TABLE.EVENT)}: ${event.eventName || I18n.t(i18nKeys.TABLE.GROUP_NO_EVENT)}`;

const CharityHeader: React.FC<{
  group: CharityGroup;
  onDownload: (group: CharityGroup) => void;
}> = ({ group, onDownload }) => {
  const readyCount = group.rows.filter(isChargeable).length;
  const blockedCount = group.rows.length - readyCount;
  // Safe to total: one charity means one currency.
  const subtotal = sumChargeableAmount(group.rows);
  // With a single event there is no reason to spend a second header row on it.
  const singleEvent = group.events.length === 1 ? group.events[0] : null;

  return (
    <Table.Row className={styles.groupHeaderRow}>
      <Table.Cell colSpan={COLUMN_COUNT}>
        <div className={styles.groupHeader}>
          <div className={styles.groupIdentity}>
            <span className={styles.groupPart}>
              <span className={styles.groupLabel}>{`${I18n.t(i18nKeys.TABLE.CHARITY)}:`}</span>
              <span className={styles.groupName} title={group.charityName || undefined}>
                {group.charityName || I18n.t(i18nKeys.TABLE.GROUP_UNLOADED)}
              </span>
            </span>
            {singleEvent && (
              <span className={styles.groupPart}>
                <span className={styles.groupLabel}>{`${I18n.t(i18nKeys.TABLE.EVENT)}:`}</span>
                <span
                  className={styles.groupName}
                  title={singleEvent.eventName || undefined}
                >
                  {singleEvent.eventName || I18n.t(i18nKeys.TABLE.GROUP_NO_EVENT)}
                </span>
              </span>
            )}
          </div>
          <div className={styles.groupFigures}>
            <span>{I18n.t(i18nKeys.TABLE.READY_COUNT, { count: readyCount })}</span>
            {blockedCount > 0 && (
              <span>{I18n.t(i18nKeys.TABLE.BLOCKED_COUNT, { count: blockedCount })}</span>
            )}
            {readyCount > 0 && (
              <span className={styles.groupSubtotal}>
                {I18n.t(i18nKeys.TABLE.GROUP_SUBTOTAL, {
                  total: formatAmountWithCurrency(subtotal, group.currency)
                })}
              </span>
            )}
            <Button
              size="mini"
              compact
              basic
              onClick={() => onDownload(group)}
              title={I18n.t(i18nKeys.TABLE.DOWNLOAD_CHARITY_HINT)}
            >
              <Icon name="download" />
              {I18n.t(i18nKeys.TABLE.DOWNLOAD_CHARITY)}
            </Button>
          </div>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

const SummaryTable: React.FC<SummaryTableProps> = ({
  groups,
  recentEntries,
  now,
  canRemove,
  showOutcome,
  onRemoveId,
  onRefreshId,
  onDownloadCharity
}) => (
  <div className={styles.tableWrapper}>
    <Table celled compact size="small" className={styles.summaryTable}>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.ID)}</Table.HeaderCell>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.NAME)}</Table.HeaderCell>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.EMAIL)}</Table.HeaderCell>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.FUNDRAISE_BY)}</Table.HeaderCell>
          <Table.HeaderCell
            className={styles.amountCell}
            title={I18n.t(i18nKeys.TABLE.AMOUNT_HINT)}
          >
            {I18n.t(i18nKeys.TABLE.AMOUNT)}
          </Table.HeaderCell>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.ELIGIBILITY)}</Table.HeaderCell>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.OUTCOME)}</Table.HeaderCell>
          <Table.HeaderCell>{I18n.t(i18nKeys.TABLE.ACTIONS)}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {groups.map((group) => (
          <React.Fragment key={group.key}>
            <CharityHeader group={group} onDownload={onDownloadCharity} />
            {group.events.map((event) => (
              <React.Fragment key={event.key}>
                {/* Only worth its own row when the charity spans more than one. */}
                {group.events.length > 1 && (
                  <Table.Row className={styles.eventHeaderRow}>
                    <Table.Cell colSpan={COLUMN_COUNT}>
                      <span className={styles.eventName}>{eventLabel(event)}</span>
                    </Table.Cell>
                  </Table.Row>
                )}
                {event.rows.map((row) => (
                  <SummaryTableRow
                    key={row.id}
                    row={row}
                    recentEntries={recentEntries}
                    now={now}
                    canRemove={canRemove}
                    showOutcome={showOutcome}
                    onRemoveId={onRemoveId}
                    onRefreshId={onRefreshId}
                  />
                ))}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </Table.Body>
    </Table>
  </div>
);

export default SummaryTable;
